<template>
    <div>
        <!-- Version warning for migrated integration -->
        <el-alert
            v-if="hasAccessToken && (salesforce_version.need_install || salesforce_version.need_to_update)"
            class="mb-3"
            type="warning"
            :closable="false"
            show-icon>
            <div class="d-block">
                <div class="mb-2 text-black">
                    <template v-if="salesforce_version.need_install">
                        Please install the Aloware Cloud Contact Center Package in Salesforce to enable calling, track call data, and access all integration features.
                    </template>
                    <template v-else>
                        A new version of Aloware Cloud Contact Center Package ({{ salesforce_version.available_version }}) is available.
                        Your current version is {{ salesforce_version.installed_version }}.
                        Please update to access the latest features and improvements.
                    </template>
                </div>
                <div class="d-flex">
                    <el-button
                        type="primary"
                        size="mini"
                        @click="openSalesforcePackage"
                        class="mr-2">
                        {{ salesforce_version.need_install ? 'Install Salesforce Package' : 'Update Salesforce Package' }}
                    </el-button>
                    <el-button
                        v-if="salesforce_version.need_activate_features"
                        type="success"
                        size="mini"
                        :loading="checking_update"
                        @click="checkPackageUpdate"
                        class="ml-2">
                        Package is updated, activate related features
                    </el-button>
                </div>
            </div>
        </el-alert>
        <template v-else>
            <div class="d-flex">
                <el-tooltip
                    class="item"
                    effect="dark">
                    <div slot="content">
                        Please install the Aloware Cloud Contact Center Package in Salesforce to enable calling, track call data, and access all integration features.
                    </div>
                    <el-button type="primary"
                               size="mini"
                               @click="openSalesforcePackage"
                               class="mr-2">
                        Install Salesforce Package
                    </el-button>
                </el-tooltip>
                <el-tooltip
                    class="item"
                    effect="dark"
                    placement="right">
                    <div slot="content">
                        <strong>Authentication</strong>
                        <br>
                        <small>
                            This will redirect you to Salesforce for authentication.
                        </small>
                    </div>
                    <el-button v-if="!hasAccessToken"
                               plain
                               size="mini"
                               @click="oauth()">
                        <span>Installed. Authenticate</span>
                        <i class="fa fa-sign-in mr-2"></i>
                    </el-button>
                </el-tooltip>
            </div>
        </template>
        <div class="d-flex my-1"
             v-if="hasAccessToken">
            <strong>To enable click-to-dial in Salesforce, download our <a href="javascript:void(0)" @click="downloadCallCenterXml" class="text-primary text-decoration-underline">Call Center XML</a> and follow the <a href="https://support.aloware.com/en/articles/10715624-cti-guidelines" class="text-primary text-decoration-underline" target="_blank">installation guide</a>.</strong>
        </div>
    </div>
</template>
<script>
export default {
    data() {
        return {
            salesforce_version: {
                loading: false,
                need_install: false,
                need_to_update: false,
                available_version: null,
                installed_version: null,
                need_activate_features: false
            },
            checking_update: false
        }
    },
    props: {
        integrationName: {
            type: String,
            required: true
        },
        oauth: {
            type: Function,
            required: true
        },
        hasAccessToken: {
            type: Boolean,
            default: false
        },
        otherCrmIsEnabled: {
            type: Boolean,
            default: false
        }
    },
    created() {
        this.checkSalesforceVersion()
    },
    methods: {
        checkSalesforceVersion() {
            if (!this.hasAccessToken) {
                return;
            }

            this.salesforce_version.loading = true;
            axios.get('/api/v1/integration/salesforce/check_version')
                .then(response => {
                    this.salesforce_version = {
                        ...response.data,
                        loading: false
                    };
                })
                .catch(error => {
                    this.$root.handleErrors(error.response);
                    this.salesforce_version.loading = false;
                });
        },
        openSalesforcePackage() {
            window.open('https://appexchange.salesforce.com/appxListingDetail?listingId=c6e09820-12d5-48f4-aa9f-3c485d3781b6', '_blank')
        },
        downloadCallCenterXml() {
            // Show notification
            this.$notify({
                offset: 95,
                title: 'Call Center XML',
                message: 'Downloading AlowareCallCenterAdapter.xml file...',
                type: 'success'
            });

            // Use axios to make an authenticated request
            axios({
                url: '/api/v1/integration/salesforce/download-callcenter-xml',
                method: 'GET',
                responseType: 'blob', // Important for handling binary data
            })
                .then(response => {
                    // Create a blob URL from the response data
                    const blob = new Blob([response.data], { type: 'application/xml' });
                    const url = window.URL.createObjectURL(blob);

                    // Create a temporary link element to trigger the download
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', 'AlowareCallCenterAdapter.xml');
                    document.body.appendChild(link);

                    // Trigger the download
                    link.click();

                    // Clean up
                    window.URL.revokeObjectURL(url);
                    document.body.removeChild(link);
                })
                .catch(error => {
                    this.$notify({
                        offset: 95,
                        title: 'Download Failed',
                        message: 'Failed to download the Call Center XML file. Please try again.',
                        type: 'error'
                    });
                    console.error('Download error:', error);
                    this.$root.handleErrors(error.response);
                });
        },
        checkPackageUpdate() {
            this.checking_update = true;
            axios.post('/api/v1/integration/salesforce/verify_package_update')
                .then(response => {
                    console.warn(response)
                    if (response.data.feature_is_activating) {
                        this.$notify({
                            title: 'Success',
                            message: 'Package update verified. Activating new features...',
                            type: 'success',
                            duration: 5000
                        });
                        setTimeout(() => {
                            window.location.reload();
                        }, 5000);
                    } else if (response.data.features_activated) {
                        this.$notify({
                            title: 'Success',
                            message: 'All features already activated',
                            type: 'success',
                            duration: 3000
                        });
                    } else {
                        this.$notify({
                            title: 'Update Required',
                            message: 'Package update not detected. Please make sure you have updated the package in Salesforce.',
                            type: 'warning'
                        });
                    }
                })
                .catch(error => {
                    this.$notify({
                        title: 'Error',
                        message: 'Failed to verify package update. Please try again.',
                        type: 'error'
                    });
                    this.$root.handleErrors(error.response);
                })
                .finally(() => {
                    this.checking_update = false;
                });
        },
    }
}
</script>
